import { translate } from "app/filters/i18nFilter";
import EcommerceSignalSubType from "app/pages/sales-intelligence/sub-modules/signals/models/ecommerce/EcommerceSignalSubType";
import {
    normalizeDirectionForForm,
    normalizePeriodForForm,
} from "app/pages/sales-intelligence/sub-modules/signals/utils/ecommerce/ecommerceFilterNormalization";
import signalsI18nKeys from "app/pages/sales-intelligence/sub-modules/signals/utils/i18n";

import type { EcommerceSignalChangeFilterDto, EcommerceSignalEventDto } from "../../../types/signals";

type EcommerceChangeHeadlineKeys = {
    withPrevious: string;
    withoutPrevious: string;
};

function formatEcommerceEventMetricValue(value: string | null): string {
    return value !== null && value !== "" ? value : "";
}

function hasEcommercePreviousMetric(value: string | null): boolean {
    return formatEcommerceEventMetricValue(value) !== "";
}

const appendMetricRangeLine = (signal: EcommerceSignalEventDto): string | undefined => {
    const prev = formatEcommerceEventMetricValue(signal.previousMetricValue);
    const next = formatEcommerceEventMetricValue(signal.newMetricValue);
    if (prev !== "" && next !== "") {
        return `${prev} → ${next}`;
    }
    return undefined;
};

function translateEcommerceChangeEventHeadline(
    change: EcommerceSignalChangeFilterDto | null,
    headlineKeys: EcommerceChangeHeadlineKeys,
    prevValue: string,
    nextValue: string,
    previousMetricRaw: string | null,
): string {
    if (!change?.direction || !change.period) {
        return "";
    }
    const direction = normalizeDirectionForForm(change.direction);
    if (!direction) {
        return "";
    }
    const period = normalizePeriodForForm(change.period);
    if (!period) {
        return "";
    }

    const directionText = translate(signalsI18nKeys.ecommerce.directionLabel(direction));
    const periodText = translate(signalsI18nKeys.ecommerce.periodLabel(period));

    if (hasEcommercePreviousMetric(previousMetricRaw)) {
        return translate(headlineKeys.withPrevious, {
            direction: directionText,
            prevValue,
            nextValue,
            period: periodText,
        });
    }

    return translate(headlineKeys.withoutPrevious, {
        direction: directionText,
        nextValue,
        period: periodText,
    });
}

export const getEcommerceSignalEventHeadline = (signal: EcommerceSignalEventDto): string => {
    const { subType, filters, previousMetricValue, newMetricValue } = signal;

    const prevValue = formatEcommerceEventMetricValue(previousMetricValue);
    const nextValue = formatEcommerceEventMetricValue(newMetricValue);

    switch (subType) {
        case EcommerceSignalSubType.OnlineRevenueChange:
            return translateEcommerceChangeEventHeadline(
                filters.change,
                {
                    withPrevious: signalsI18nKeys.ecommerce.eventHeadline.onlineRevenueChange,
                    withoutPrevious: signalsI18nKeys.ecommerce.eventHeadline.onlineRevenueChangeNoPrevious,
                },
                prevValue,
                nextValue,
                previousMetricValue,
            );
        case EcommerceSignalSubType.OnlineTransactionsChange:
            return translateEcommerceChangeEventHeadline(
                filters.change,
                {
                    withPrevious: signalsI18nKeys.ecommerce.eventHeadline.onlineTransactionsChange,
                    withoutPrevious: signalsI18nKeys.ecommerce.eventHeadline.onlineTransactionsChangeNoPrevious,
                },
                prevValue,
                nextValue,
                previousMetricValue,
            );
        case EcommerceSignalSubType.OnlineRevenueReached: {
            if (typeof filters.bucketTransition?.bucket !== "string") {
                return "";
            }

            return translate(signalsI18nKeys.ecommerce.eventHeadline.onlineRevenueReached, {
                nextValue,
            });
        }
        case EcommerceSignalSubType.OnlineTransactionsReached: {
            if (typeof filters.bucketTransition?.bucket !== "string") {
                return "";
            }

            return translate(signalsI18nKeys.ecommerce.eventHeadline.onlineTransactionsReached, {
                nextValue,
            });
        }
        default:
            return "";
    }
};

export const getEcommerceSignalEventInfoText = (signal: EcommerceSignalEventDto): string => {
    const headline = getEcommerceSignalEventHeadline(signal);
    if (headline) {
        return headline;
    }

    return appendMetricRangeLine(signal) ?? "";
};
